import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import OrderCustomizationSummary from '../orders/OrderCustomizationSummary';
import * as FiIcons from 'react-icons/fi';

const {
  FiCalendar,
  FiCreditCard,
  FiHash,
  FiMapPin,
  FiTruck,
  FiX
} = FiIcons;

const toFiniteNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const normalized = value.replace(/[^0-9.-]/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (value && typeof value === 'object') {
    return toFiniteNumber(
      value.amount ?? value.value ?? value.price ?? value.unit_price
    );
  }

  return 0;
};

const money = (value, currency = 'cad') =>
  `${currency.toUpperCase()} ${toFiniteNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const signedMoney = (value, currency = 'cad') => {
  const amount = toFiniteNumber(value);
  const prefix = amount > 0 ? '+' : amount < 0 ? '-' : '';

  return `${prefix}${money(Math.abs(amount), currency)}`;
};

const getItemQuantity = (item) => Math.max(1, toFiniteNumber(item.quantity) || 1);

const getUnitPrice = (item) => {
  const explicitBasePrice = item.base_price
    ?? item.base_unit_price
    ?? item.original_unit_price;

  if (explicitBasePrice !== undefined && explicitBasePrice !== null) {
    return toFiniteNumber(explicitBasePrice);
  }

  const quantity = getItemQuantity(item);
  const customizationTotal = toFiniteNumber(
    item.customization_total
      ?? item.customization_price
      ?? item.customization_adjustments_total
      ?? item.option_adjustment
      ?? item.customization_adjustment
  );

  return Math.max(
    0,
    toFiniteNumber(item.unit_price ?? item.price ?? item.amount)
      - customizationTotal / quantity
  );
};

const getCustomizationTotal = (item) => toFiniteNumber(
  item.customization_total
    ?? item.customization_price
    ?? item.customization_adjustments_total
    ?? item.option_adjustment
    ?? item.customization_adjustment
);

const PriceBreakdown = ({ item, currency }) => {
  const quantity = getItemQuantity(item);
  const unitPrice = getUnitPrice(item);
  const customizationTotal = getCustomizationTotal(item);
  const customizationUnitPrice = customizationTotal / quantity;
  const lineTotal = unitPrice * quantity;

  return (
    <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50/60 p-3">
      <p className="text-[9px] font-black uppercase tracking-widest text-indigo-500">
        Price breakdown
      </p>

      <div className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
        <PriceRow
          label="Base unit price before customizations"
          value={money(unitPrice, currency)}
        />
        <PriceRow
          label="Customization / unit"
          value={signedMoney(customizationUnitPrice, currency)}
          accent
        />
        <PriceRow
          label="Total for base units"
          value={`${quantity} × ${money(unitPrice, currency)} = ${money(lineTotal, currency)}`}
          strong
        />
      </div>

      <div className="mt-3 border-t border-indigo-100 pt-3">
        <div className="flex items-center justify-between gap-3 text-xs">
          <span className="font-bold text-slate-600">
            Total customization price
          </span>
          <span className="font-black text-indigo-700">
            {signedMoney(customizationTotal, currency)}
          </span>
        </div>

        {item.option_summary && (
          <p className="mt-2 text-[10px] font-bold leading-relaxed text-slate-500">
            {item.option_summary}
          </p>
        )}
      </div>
    </div>
  );
};

const isAvailable = (value) => value !== null && value !== undefined;

const Finance = ({ label, value, currency, pending = false }) => (
  <div className="rounded-2xl border border-slate-100 p-4">
    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
      {label}
    </p>
    <p className={`mt-1 text-lg font-black ${
      pending ? 'text-amber-600' : 'text-slate-900'
    }`}>
      {pending ? 'Pending calculation' : money(value, currency)}
    </p>
  </div>
);

const AdminOrderDetailsModal = ({ order, onClose }) => {
  if (!order) return null;

  const currency = order.stripe_currency || 'cad';
  const items = Array.isArray(order.items) ? order.items : [];
  const taxPending = (
    !isAvailable(order.stripe_tax_amount_cents)
    && order.stripe_tax_status !== 'not_required'
  );
  const feePending = !isAvailable(order.stripe_processing_fee_cents);
  const sellerNetPending = !isAvailable(order.seller_net_earnings_cents);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[2.5rem] bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500">
              Order details
            </p>
            <h2 className="mt-1 text-2xl font-black text-slate-900">
              #{String(order.id).slice(0, 8).toUpperCase()}
            </h2>
            <p className="mt-1 text-xs font-bold text-slate-400">
              {order.buyer_name} · {order.seller_business_name}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-slate-400 hover:bg-slate-50">
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <Info icon={FiCalendar} label="Fulfillment">
            {order.requested_date || 'Not specified'} {order.requested_time || ''}
          </Info>
          <Info icon={order.delivery_method === 'delivery' ? FiTruck : FiMapPin} label="Method">
            {order.delivery_method || 'Pickup'}
          </Info>
          <Info icon={FiCreditCard} label="Payment">
            {order.payment_status || 'unpaid'} · {order.payment_mode || 'direct'}
          </Info>
          <Info icon={FiHash} label="Status">
            {order.status || 'unknown'}
          </Info>
        </div>

        <div className="mt-6 rounded-2xl bg-slate-50 p-5">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            Items and customizations
          </h3>
          <div className="mt-3 space-y-3">
            {items.length ? items.map((item, index) => (
              <React.Fragment key={`${item.title || 'item'}-${index}`}>
                <div key={`${item.title || 'item'}-${index}`} className="flex justify-between gap-4 rounded-xl bg-white p-3">
                  <div>
                    <p className="text-sm font-black text-slate-900">
                      {item.quantity || 1} × {item.title || 'Item'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {item.special_instructions || item.instructions || 'No special instructions'}
                    </p>
                  </div>
                  <span className="text-sm font-black text-slate-900">
                    {money(getUnitPrice(item), currency)}
                  </span>
                </div>
                <PriceBreakdown item={item} currency={currency} />
                <OrderCustomizationSummary item={item} />
              </React.Fragment>
            )) : (
              <p className="text-sm font-bold text-slate-400">No item detail was stored.</p>
            )}
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <Finance
            label="Product subtotal"
            value={order.product_subtotal_cents != null
              ? Number(order.product_subtotal_cents) / 100
              : order.total_amount}
            currency={currency}
          />
          <Finance
            label="Stripe tax"
            value={order.stripe_tax_amount_cents != null
              ? Number(order.stripe_tax_amount_cents) / 100
              : null}
            pending={taxPending}
            currency={currency}
          />
          <Finance
            label="Stripe fee"
            value={order.stripe_processing_fee_cents != null
              ? Number(order.stripe_processing_fee_cents) / 100
              : null}
            pending={feePending}
            currency={currency}
          />
          <Finance
            label="Seller net"
            value={order.seller_net_earnings_cents != null
              ? Number(order.seller_net_earnings_cents) / 100
              : null}
            pending={sellerNetPending}
            currency={currency}
          />
        </div>

        {order.stripe_finance_pending_reason && (
          <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-xs font-bold text-amber-800">
            {order.stripe_finance_pending_reason}
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-slate-100 p-5 text-xs text-slate-500">
          <p className="font-black uppercase tracking-widest text-slate-400">Stripe references</p>
          <p className="mt-2 break-all">Checkout: {order.stripe_checkout_session_id || 'Not recorded'}</p>
          <p className="mt-1 break-all">PaymentIntent: {order.stripe_payment_intent_id || 'Not recorded'}</p>
          {order.stripe_tax_review_required && (
            <p className="mt-3 font-bold text-amber-700">
              Tax review: {order.stripe_tax_review_reason || 'Stripe tax calculation requires review.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const Info = ({ icon, label, children }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-slate-100 p-4">
    <SafeIcon icon={icon} className="text-indigo-600" />
    <div>
      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-bold capitalize text-slate-900">{children}</p>
    </div>
  </div>
);

const PriceRow = ({ label, value, accent = false, strong = false }) => (
  <div>
    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
      {label}
    </p>
    <p className={`mt-1 font-black ${
      strong
        ? 'text-slate-900'
        : accent
          ? 'text-indigo-600'
          : 'text-slate-700'
    }`}>
      {value}
    </p>
  </div>
);

export default AdminOrderDetailsModal;